'use client'
import { sendGTMEvent } from '@next/third-parties/google'

import { ChevronRight, CirclePlus, X } from 'lucide-react'
import Image from 'next/image'
import Link from 'next/link'
import { useEffect, useRef, useState } from 'react'
import { twMerge } from 'tailwind-merge'
import { baseUrl, bffUrl } from '@/helpers/urls'
import type { AreaType } from '@/types/area-type'
import type { CourseType } from '@/types/course-type'
import ProductCardGroup from '@/app/(common)/cursos/(products)/_components/product-card-group'
import { Button } from '@/components/ui/button'
import { bff_client } from '@/lib/apiClient'
import plusIcon from '@/public/icons/plus.svg'
import { buildGA4EventModel } from '@/components/@analitycs/push-ecommerce-event'
import ProductCard from '../../cursos/product-card'
import { ProductsNotFoundWarn } from '../../global/products-not-found-warn'
import CourseCardSkeleton from '../../skeleton/course-card-skeleton'

interface SearchProductsProps {
  termSearch: string
  searchQuery: string
  isSearchVisible: boolean
  onClose: () => void
}

export default function SearchProducts({
  termSearch,
  isSearchVisible,
  onClose,
  searchQuery,
}: SearchProductsProps) {
  const [areas, setAreas] = useState<AreaType[]>([])
  const [products, setProducts] = useState<CourseType[]>([])
  const [productName, setProductName] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const abortControllerRef = useRef<AbortController | null>(null)
  const isFirstRender = useRef(true)

  // listando areas
  useEffect(() => {
    ;(async () => {
      // let data = await fetch(bffUrl('/areas?per_page=8')).then(res => res.json())
      // data = data.data
      // setAreas(data)

      const responseAreas = await bff_client.get('areas', {
        searchParams: {
          per_page: 8,
        },
      })
      const areas: { data: AreaType[] } = await responseAreas.json()
      setAreas(areas.data)
    })()
  }, [])

  // controlar do tempo da requisição da busca por nome do curso
  useEffect(() => {
    const handler = setTimeout(
      () => setProductName(searchQuery ?? termSearch),
      500
    )
    return () => clearTimeout(handler)
  }, [termSearch, searchQuery])

  // listado cursos da busca
  useEffect(() => {
    const fetchProduct = async () => {
      // Cancela requisição anterior se existir
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }

      abortControllerRef.current = new AbortController()

      try {
        // Envia evento de busca apenas se não for o primeiro render
        // e se houver termo de busca
        if (!isFirstRender.current && productName.length > 0) {
        }

        // Sempre busca cursos, com ou sem termo de busca
        // const querySearch = productName.length > 0
        //   ? `search=${productName}`
        //   : 'per_page=8';

        const querySearch: Record<string, string | number | boolean> = {
          sort: 'popular',
          ...(productName.length > 0
            ? { search: productName }
            : { per_page: 8 }),
        }

        // const response = await fetch(`/products?${querySearch}`,
        //   { signal: abortControllerRef.current.signal }
        // );
        const responseProducts = await bff_client.get('products', {
          searchParams: {
            ...querySearch,
            status: 'actived',
          },
          signal: abortControllerRef.current.signal,
        })

        if (!responseProducts.ok) throw new Error('Failed to fetch courses')

        const products: { data: CourseType[] } = await responseProducts.json()
        const productsList = products.data || []

        // Envia evento de visualização apenas se tiver resultados
        // if (coursesList.length > 0) {
        //   window.dataLayer = window.dataLayer || [];
        //   window.dataLayer.push({
        //     event: 'view_item_list',
        //     items: coursesList.map((course) => ({
        //       item_id: course.id,
        //       item_name: course.name,
        //     })),
        //   });
        // }

        setProducts(productsList)
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') {
          // Ignora erro de requisição cancelada
          return
        }
        console.error('Error fetching courses:', error)
        setProducts([])
      } finally {
        isFirstRender.current = false
        setIsLoading(false)
      }
    }

    if (isSearchVisible) {
      setIsLoading(true)
      fetchProduct()
    }

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [productName, isSearchVisible])

  // Reset loading when search becomes visible
  useEffect(() => {
    if (isSearchVisible) {
      setIsLoading(true)
    }
  }, [isSearchVisible])

  const hasProducts = products.length > 0

  return (
    <div
      className={twMerge(
        '-translate-y-40 invisible fixed inset-0 z-20 overflow-y-auto bg-gray-50 opacity-0 transition duration-500',
        isSearchVisible && 'visible translate-y-0 opacity-100'
      )}
    >
      {/* Header com o botão de fechar */}
      <div className="container min-h-screen pt-72 pb-36 lg:pt-36">
        <div>
          <div className="flex items-center justify-between">
            {hasProducts && (
              <h1 className="font-medium text-2.5xl text-neutral-900">
                Cursos Populares
              </h1>
            )}

            <button
              className="ml-auto hidden lg:block"
              onClick={onClose}
              type="button"
            >
              <X className="size-6 text-[#ABACAD]" />
            </button>
          </div>
          <div className="mt-3">
            {isLoading && (
              <div className="mt-6 mb-9 grid w-full grid-cols-1 gap-x-5 gap-y-8 md:grid-cols-2 lg:grid-cols-4">
                {Array.from({ length: 8 }).map((_, i) => (
                  <CourseCardSkeleton key={i} />
                ))}
              </div>
            )}

            {hasProducts && (
              // <ProductCardGroup products={products} columns={4} />

              <div className="mt-6 mb-9 grid w-full grid-cols-1 justify-start gap-x-5 gap-y-8 md:grid-cols-2 lg:grid-cols-4">
                {products.map(product => (
                  <button
                    key={product.reference}
                    onClick={onClose}
                    type="button"
                  >
                    <ProductCard product={product} />
                  </button>
                ))}
              </div>
            )}

            {!(hasProducts || isLoading) && productName.length === 0 && (
              <div className="mt-6 mb-9 grid w-full grid-cols-1 gap-x-5 gap-y-8 md:grid-cols-2 lg:grid-cols-4">
                {Array.from({ length: 8 }).map((_, i) => (
                  <CourseCardSkeleton key={i} />
                ))}
              </div>
            )}

            {!(hasProducts || isLoading) && productName.length > 0 && (
              <ProductsNotFoundWarn searchTerm={productName} />
            )}

            {/* {products.map(product => (
              <button
                key={product.reference}
                type="button"
                onClick={onClose}
                className="w-full lg:w-max"
              >
                <ProductCard product={product} />
              </button>
            ))} */}
          </div>
        </div>
        {/* Conteúdo dos cursos populares */}
        <div className="mt-9">
          <div className="flex items-center justify-between">
            <h2 className="font-medium text-lg text-neutral-900 md:text-2.5xl">
              Tópicos mais pesquisados
            </h2>
            <button
              className="group h-10 max-w-96 rounded border border-primary-700 bg-transparent hover:bg-primary-700 hover:transition hover:duration-300"
              onClick={onClose}
              type="button"
            >
              <Link
                className="flex h-10 max-w-96 items-center gap-2 px-4 font-semibold text-primary-700 text-sm group-hover:text-white group-hover:transition group-hover:duration-300"
                href="/cursos"
              >
                <CirclePlus size={18} />
                Ver todos os cursos
              </Link>
            </button>
          </div>
          <div className="mt-4 flex flex-wrap items-center justify-start gap-x-4 gap-y-2 pb-17 md:gap-x-2.5 md:pb-8">
            {areas.map(area => (
              <Button
                asChild
                className="group"
                key={area.reference}
                onClick={onClose}
                size="xl"
                type="button"
                variant="category"
              >
                {/* licando para area */}
                <Link href={`/cursos/${area.url}`} key={area.reference}>
                  <div className="flex w-full items-center gap-2">
                    <span className="font-medium text-gray-800 text-lg md:text-2xl">
                      {area.name}
                    </span>
                    <div className="rounded-full border border-[#E6E6E8] bg-transparent p-2 group-hover:rotate-45">
                      <ChevronRight className="size-5" />
                    </div>
                  </div>
                </Link>
              </Button>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
